import { useAuthStore } from '@/store/auth';
import { useSmeAuthStore } from '@/store/sme/sme-auth';
import axios from 'axios';
import constants from '@/constants';
import usePlatformAnalytics from '@/composables/usePlatformAnalytics';

const apiBaseUrl = constants.apiBaseUrl;

const redirectNotAuth = (isSme?: boolean) => {
  const here = window.location.pathname;

  if (isSme) {
    if (!['/sme/', '/sme/auth/signin', '/sme/auth/signup'].includes(here)) {
      window.location.href = `/sme/auth/signin?redirect=${here}`;
    }
    return;
  } else {
    if (!['/', '/auth/login', '/auth/signup'].includes(here)) {
      window.location.href = `/auth/login?redirect=${here}`;
    }
  }
};

// concatenate the baseUrl to the url path
const fetchUrl = (base: string, path: string) => base + path;

export function request(method: string, baseUrl: string = apiBaseUrl, useSmeAuth: boolean = false) {
  // returns a http promise
  return async function (url: string, _body?: any, _headers?: any, responseType?: string, params?: any) {
    const authStore = useAuthStore();
    const smeAuthStore = useSmeAuthStore();
    const { trackEvent } = usePlatformAnalytics(useSmeAuth);

    const fullUrl = params ? `${fetchUrl(baseUrl, url)}?${new URLSearchParams(params)}` : fetchUrl(baseUrl, url);

    // Use SME token if specified, otherwise use base token
    const token = useSmeAuth ? smeAuthStore.smeToken : authStore.token;

    const headers = {
      ...(token && { Authorization: `Bearer ${token}` }),
      'Content-Type': `application/json`,
      ..._headers,
    };

    try {
      const res = await axios({ url: fullUrl, method, headers, data: _body });

      if (res?.status === 200 || res?.status === 201) {
        return res.data;
      } else if (res?.status === 401) {
        if (useSmeAuth) {
          smeAuthStore.clearSmeToken();
        } else {
          authStore.clearToken();
        }
        redirectNotAuth(useSmeAuth);
      } else {
        const error = res?.data;
        if (error?.message) throw error?.message;
        else throw error?.error;
      }
    } catch (e: any) {
      // --- Backwards-compatible enhanced error ---
      const errorValue = e.response?.data?.value ?? e.value;
      const message = e.response?.data?.message ?? e.response?.data?.error ?? e.message ?? 'An unknown error occurred';

      trackEvent({
        event_name: 'error',
        platform: useSmeAuth ? 'sme' : 'adv',
        metadata: {
          error_message: message,
          error_http_status: String(e.response?.status),
          resource: fullUrl,
        },
      });

      if (e.response?.status === 401) {
        if (useSmeAuth) {
          smeAuthStore.clearSmeToken();
        } else {
          authStore.clearToken();
        }
        redirectNotAuth(useSmeAuth);
      }
      if (e.response?.status === 429) {
        throw e.response?.status;
      }

      // Maintain backwards compatibility with Error.message
      const errorObj = new Error(message) as Error & { value?: any };
      errorObj.value = errorValue;

      throw errorObj;
    }
  };
}

// Requests made to the monolith (base auth)
export const get = request('GET');
export const post = request('POST');
export const put = request('PUT');
export const patch = request('PATCH');
export const del = request('DELETE');

// Requests for SME auth
export const getSme = request('GET', apiBaseUrl, true);
export const postSme = request('POST', apiBaseUrl, true);
export const putSme = request('PUT', apiBaseUrl, true);
export const patchSme = request('PATCH', apiBaseUrl, true);
export const delSme = request('DELETE', apiBaseUrl, true);
